import React, { PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { useTracking } from "react-tracking";

import { useQueryClient } from "@tanstack/react-query";

import { authApi, userApi } from "api";
import { markDashboardVisited } from "api/user/user";
import { TrackingContext, TrackingLoginData } from "contexts/TrackingContext/TrackingContext";
import useFetchMyProfile from "hooks/api/userProfiles/useFetchMyProfile/useFetchMyProfile";
import useEffectOnce from "hooks/useEffectOnce";
import { IUser, transformToUserClass, User } from "models/User";
import { AuthRedirect } from "types/auth";
import { getUserBusinessType } from "utils/tracking/props/ga4/user";

const { fetchSession } = userApi;
const { refreshAccessToken } = authApi;

export interface SessionContextInterface {
  user: null | User;
  setUser: (user: User) => void;
  login: (data: null | IUser) => void;
  refreshSession: () => void;
  authRedirect?: null | AuthRedirect;
  setAuthRedirect: (authRedirect: null | AuthRedirect) => void;
}

export const DefaultSessionContext = {
  user: null,
  setUser: () => null,
  login: () => null,
  refreshSession: () => null,
  authRedirect: null,
  setAuthRedirect: () => null,
};

export const SessionContext = React.createContext<SessionContextInterface>(DefaultSessionContext);

const refreshUser = async () => {
  return refreshAccessToken().then(() => {
    return fetchSession(false);
  });
};

const initializeSession = async (setUser) => {
  const [error, response] = await refreshUser();
  const data = error ? null : response.user;

  setUser(transformToUserClass(data));
};

const SessionContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { loginData } = useContext(TrackingContext);
  const [user, setUser] = useState<undefined | null | User>();
  const [authRedirect, setAuthRedirect] = useState<undefined | null | AuthRedirect>();
  const queryClient = useQueryClient();
  const { data: userProfile } = useFetchMyProfile({ enabled: !!user });
  const { trackEvent } = useTracking();

  useEffectOnce(() => {
    initializeSession(setUser);
  });

  const login = (data: null | IUser) => {
    // remove all queries from the cache when new user logs in
    // so the state from the previous customer does not leak
    queryClient.invalidateQueries();

    if (data) {
      setUser(transformToUserClass(data));
    } else {
      setUser(null);
    }
  };

  const refreshSession = async () => {
    await initializeSession(setUser);
  };

  // marks dashboard visited for user
  useEffect(() => {
    if (user && userProfile?.policy.hasCoverage && !userProfile?.policy.dashboard?.dashboardVisited) {
      // TODO: needs reorganizing to respond with new state, should be done when refactoring user policy data handling
      markDashboardVisited();
    }
  }, [user, userProfile]);

  // tracks user log in
  const loginTrackedUserRef = useRef<TrackingLoginData | null>(null);
  useEffect(() => {
    if (loginData && userProfile && !loginTrackedUserRef.current) {
      loginTrackedUserRef.current = loginData;

      trackEvent({
        name: "login",
        method: loginData.method,
        user_id: loginData.user.id,
        business_type: getUserBusinessType(userProfile),
      });
    }

    if (!loginData && loginTrackedUserRef.current) {
      loginTrackedUserRef.current = null;
    }
  }, [loginData, userProfile]);

  // TODO: this causes app to not render at all while /users/me is being called
  return user === undefined ? null : (
    <SessionContext.Provider value={{ user, setUser, login, refreshSession, authRedirect, setAuthRedirect }}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionContextProvider;
